import { Box, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { formatNumberDE } from '../../../../../../shared/utils/formatNumber';

// Greyscale monochromatic palette for hold categories
const CHART_COLORS = [
  '#616161', // grey 700
  '#9E9E9E', // grey 500
  '#BDBDBD', // grey 400
  '#757575', // grey 600
  '#E0E0E0', // grey 300
  '#424242', // grey 800
];

/**
 * Renders a horizontal stacked bar chart showing blocked car volumes per hold,
 * with a compact legend row below.
 */
const TotalBlockedCarChart = ({ holdblockedMapping, sourceDate }) => {
  const holdEntries = Object.values(holdblockedMapping).sort((a, b) =>
    (a.shortName || a.name).localeCompare(b.shortName || b.name)
  );
  const totalBlockedCars = holdEntries.reduce(
    (sum, h) => sum + h.totalVolume,
    0
  );

  const seriesData = holdEntries.map((holdData, index) => ({
    label: holdData.shortName
      ? `${holdData.shortName} (${holdData.name})`
      : holdData.name,
    data: [holdData.totalVolume],
    stack: 'blocked',
    color: CHART_COLORS[index % CHART_COLORS.length],
  }));

  return (
    <Stack direction='column' spacing={0.25} width='100%' py={0.25}>
      {/* Stacked bar */}
      <Box width='100%' height={16} overflow='hidden' position='relative'>
        <BarChart
          xAxis={[{ id: 'x', min: 0, max: totalBlockedCars, hide: true }]}
          yAxis={[
            {
              id: 'y',
              data: [
                sourceDate
                  ? `Total · Carried forward from ${sourceDate}`
                  : 'Total',
              ],
              scaleType: 'band',
              hide: true,
            },
          ]}
          series={seriesData}
          height={16}
          layout='horizontal'
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
          barLabel={() => ({ show: false })}
          categoryGapRatio={0}
          barGapRatio={0}
          sx={{
            '& .MuiChartsAxis-root': { display: 'none' },
            '& .MuiChartsLegend-root': { display: 'none' },
          }}
        />
        <Typography
          fontWeight='medium'
          variant='caption'
          color='text-primary'
          px={0.5}
          sx={{
            fontSize: '0.65rem',
            left: 2,
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(18, 18, 18, 0.5)'
                : 'rgba(255, 255, 255, 0.5)',
          }}
        >
          {formatNumberDE(totalBlockedCars)}
        </Typography>
      </Box>

      {/* Legend */}
      <Stack direction='row' flexWrap='nowrap' gap={0.25} overflow='hidden'>
        {holdEntries.map((holdData, index) => (
          <Box
            key={holdData.name}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              px: 0.5,
              py: 0,
              borderRadius: 0.5,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
              }}
            />
            <Typography
              variant='caption'
              sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}
            >
              {holdData.shortName || holdData.name}:{' '}
              {formatNumberDE(holdData.totalVolume)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};

export default TotalBlockedCarChart;
